/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
// src/components/ResourceManager/Hardware/HardwareInventoryPage.tsx

import React, { useState, useEffect, useMemo } from "react";
import { useAuth0 } from "@auth0/auth0-react";

import {
  addHardwareResource,
  updateHardwareResource,
  deleteHardwareResource,
  getDataCentersIdsNames,
  getAllHardware,
  getHardwareByDatacenterId,
  getPortsByHardwareId,
  getHardwareTypes,
  getSubTypesByTypeHardware,
  DataCenterItem,
  Hardware,
  HardwareType,
  getHardwareById,
  AddHardwareResourcePayload,
} from "./request/request";

import { getHardwareListByDataCenter } from "../Port/request/request";
import { GenericTable } from "@ui-components/Table/GenericTable";
import GenericButton from "@ui-components/Button/GenericButton";
import GenericAlert from "@ui-components/Alert/GenericAlert";
import GenericDropdown from "@ui-components/Dropdown/GenericDropdown";

import { ReactComponent as IconPlus } from "../../../../shared/assets/Icons/IconPlusNV.svg";
import { getHardwareColumns } from "./HardwareTableColumns";
import GenericCard from "@ui-components/Card/GenericCard";
import GenericSearchbar from "@ui-components/Searchbar/GenericSearchbar";
import { columns as baseColumns } from "./HardwareData";
import { useAppSelector } from "@redux/hooks";
import { ParsedFilter as OldParsed, FilterField, buildPredicate, labelId, parseToken } from "../../../helpers/advancedFilters";
import { HardwareEditModal, HardwareViewModal, MyHardwareForm } from "./HardwareModals";
import { TableSkeleton } from "@ui-components/Skeleton/GenericSkeleton";

interface HardwareDropdownItem {
  id: number;
  hostname: string;
}
interface PortItem {
  id: number;
  portName: string;
  portNumber: string;
}
// Quitamos “name” y agregamos “subType”
const defaultHwForm: MyHardwareForm = {
  typeHardwareId: "",
  subtypeHardwareId: "",
  datacenterId: "",
  typeHardware: "",
  serialNumber: "",
  model: "",
  vendor: "",
  hostname: "",
  installedDate: "",
  endOfLifeDate: "",
  rackId: "",
  rackUnit: "",
  status: "",
  phase: "",
  template: "",
  subtypeHardware: "",
};

export default function HardwareInventoryPage() {
  // ===== 1) ESTADOS =====
  const [hardwareList, setHardwareList] = useState<Hardware[]>([]);
  const [isFetching, setIsFetching] = useState(false);
  const [isViewLoading, setIsViewLoading] = useState(false);
  const [isEditLoading, setIsEditLoading] = useState(false);
  const [isCreating, setIsCreating] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [isLoadingSubtypes, setIsLoadingSubtypes] = useState(false);
  const [isLoadingHostnames, setIsLoadingHostnames] = useState(false);

  // Form “hwForm”
  const [hwForm, setHwForm] = useState<MyHardwareForm>(defaultHwForm);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editMode, setEditMode] = useState(false);

  // View hardware
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);
  const [selectedHardware, setSelectedHardware] = useState<Hardware | null>(
    null
  );

  // Alert Delete
  const [isAlertOpen, setIsAlertOpen] = useState(false);
  const [hardwareToDelete, setHardwareToDelete] = useState<Hardware | null>(
    null
  );
  const [isDeleting, setIsDeleting] = useState(false);


  // Auth0
  const { getAccessTokenSilently, isLoading, isAuthenticated } = useAuth0();

  // DataCenters
  const [dataCenters, setDataCenters] = useState<DataCenterItem[]>([]);
  const [dataCenterOptions, setDataCenterOptions] = useState<string[]>([]);
  const [selectedDataCenterName, setSelectedDataCenterName] = useState("All");

  // For "Module" type only:
  const [hostnameList, setHostnameList] = useState<HardwareDropdownItem[]>([]);
  const [hostnameOptions, setHostnameOptions] = useState<string[]>([]);
  const [hostnameId, setHostnameId] = useState<number | null>(null);

  // For slot assignment
  const [slotList, setSlotList] = useState<PortItem[]>([]);
  const [slotOptions, setSlotOptions] = useState<string[]>([]);
  const [slotId, setSlotId] = useState<number | null>(null);

  const [activeFilters, setActiveFilters] = useState<OldParsed[]>([]);
  const { tenantId, partnerId } = useAppSelector((state) => state.selection);
  const isViewingAllCustomers = tenantId === partnerId;

  // Rows per page state for UI table pagination (default 10)
  const [rowsPerPage, setRowsPerPage] = useState(10);

  // ===== 2) NUEVO: “Type” y “SubType” =====
  // Mantenemos la lista entera de TypeHardware con {id, name} para buscar su ID al seleccionar.
  const [typeHardwareList, setTypeHardwareList] = useState<HardwareType[]>([]);
  const typeHardwareOptions = useMemo(
    () => typeHardwareList.map((t) => t.name),
    [typeHardwareList]
  );

  // Subtypes => al escoger un Type, llamamos a getSubTypesByTypeHardware
  const [subTypeList, setSubTypeList] = useState<HardwareType[]>([]);
  const subTypeOptions = useMemo(
    () => subTypeList.map((s) => s.name),
    [subTypeList]
  );

  // ===== 3) EFECTOS =====
  // 3.1) Cargar DataCenters y Types
  useEffect(() => {
    if (isLoading || !isAuthenticated) return;

    (async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();

        // 1) DataCenters
        const respDC = await getDataCentersIdsNames(token);
        // Response shape: { success, message, data: [...] }
        const dcRaw = (respDC as any)?.data?.data ?? (respDC as any)?.data ?? [];
        setDataCenters(dcRaw);
        // Create dropdown options as "datacenterId - displayName", with "All" as first option
        const options = ["All", ...dcRaw.map((dc: DataCenterItem) => 
          `${dc.datacenterId} - ${dc.displayName}`
        )];
        setDataCenterOptions(options);

        // 2) Types
        const respType = await getHardwareTypes(token);
        // Response shape: { success, message, data: [...] }
        const typesRaw = (respType as any)?.data?.data ?? (respType as any)?.data ?? [];
        // Guardar la lista de {id, name}
        setTypeHardwareList(typesRaw);
      } catch (error) {
        console.error("Error fetching data centers or hardware types:", error);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [isLoading, isAuthenticated, getAccessTokenSilently]);

  // 3.2) Al cambiar DC => cargar hardware
  useEffect(() => {
    if (!isAuthenticated) return;

    // If "All" is selected, fetch all hardware without filter
    if (selectedDataCenterName === "All") {
      (async () => {
        try {
          setIsFetching(true);
          const token = await getAccessTokenSilently();
          // Fetch all hardware without any filter
          const resp = await getAllHardware(token);
          const hwRaw = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
          setHardwareList(hwRaw);
        } catch (err) {
          console.error("Error fetching all hardware:", err);
          setHardwareList([]);
        } finally {
          setIsFetching(false);
        }
      })();
      return;
    }

    // If a specific datacenter is selected, we need datacenters to be loaded first
    if (dataCenters.length === 0) return;

    // Find datacenter by matching the concatenated string "datacenterId - displayName"
    const foundDC = dataCenters.find(
      (dc: DataCenterItem) =>
        `${dc.datacenterId} - ${dc.displayName}` === selectedDataCenterName
    );

    if (!foundDC) {
      setHardwareList([]);
      return;
    }

    (async () => {
      try {
        setIsFetching(true);
        const token = await getAccessTokenSilently();
        const resp = await getHardwareByDatacenterId(
          token,
          foundDC.datacenterId
        );
        const hwRaw = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
        setHardwareList(hwRaw);
      } catch (err) {
        console.error("Error fetching hardware by DC:", err);
        setHardwareList([]);
      } finally {
        setIsFetching(false);
      }
    })();
  }, [
    selectedDataCenterName,
    dataCenters,
    isAuthenticated,
    getAccessTokenSilently,
  ]);
  async function fetchHostnameOptions(dcId: string) {
    try {
      setIsLoadingHostnames(true);
      const token = await getAccessTokenSilently();
      const resp = await getHardwareListByDataCenter(token, dcId);

      // Store the full objects for ID lookups
      setHostnameList(resp.data);
      // Also build the string[] for the dropdown
      setHostnameOptions(
        Array.from(
          new Set(resp.data.map((hw: HardwareDropdownItem) => hw.hostname))
        )
      );
    } catch (err) {
      console.error("Error fetching hostnames =>", err);
    } finally {
      setIsLoadingHostnames(false);
    }
  }
  async function fetchSlotAssignment(hwId: number) {
    // Only fetch if the edit modal is open (to avoid unnecessary requests)
    if (!isModalOpen || !editMode) return;
    
    try {
      const token = await getAccessTokenSilently();
      const resp = await getPortsByHardwareId(token, hwId.toString());
      // Response structure: { success, message, data: { id, ..., ports: [...] } }
      const rawData = (resp as any)?.data?.data ?? (resp as any)?.data;
      const portsArray = rawData?.ports || [];
      // Map to PortItem format (portDescription -> portName)
      const mappedPorts = portsArray.map((p: any) => ({
        id: p.id,
        portName: p.portDescription || "",
        portNumber: p.portNumber || "",
      }));
      setSlotList(mappedPorts);
      // build the string array
      setSlotOptions(
        mappedPorts.map((p: PortItem) => `${p.portNumber} - ${p.portName}`)
      );
    } catch (err) {
      console.error("Error fetching slot assignments =>", err);
    }
  }

  function handleSlotSelect(label: string) {
    const match = slotList.find(
      (p) => `${p.portNumber} - ${p.portName}` === label
    );
    if (!match) return;
    setSlotId(match.id);
  }
  function getSlotLabelById(id: number): string {
    const match = slotList.find((p) => p.id === id);
    return match ? `${match.portNumber} - ${match.portName}` : "";
  }

  // ===== 4) HANDLERS HARDWARE =====
  const handleDataCenterSelect = (selected: string) => {
    setSelectedDataCenterName(selected);
  };

  const handleModalDataCenterSelect = (dcName: string) => {
    // Find the datacenter by matching the concatenated string "datacenterId - displayName"
    const foundDC = dataCenters.find(
      (dc: DataCenterItem) =>
        `${dc.datacenterId} - ${dc.displayName}` === dcName
    );
    if (foundDC) {
      handleFieldChange("datacenterId", foundDC.datacenterId);
      // If it's a Module type and we have a datacenter, fetch hostname options
      if (hwForm.typeHardware === "Module" && foundDC.datacenterId) {
        fetchHostnameOptions(foundDC.datacenterId);
      }
    }
  };
  function handleHostnameSelect(chosenName: string) {
    const match = hostnameList.find((hw) => hw.hostname === chosenName);
    if (!match) return;
    setHostnameId(match.id); // Store the numeric hardware ID
    handleFieldChange("hostname", match.hostname);
    fetchSlotAssignment(match.id);
  }
  function getHostnameLabelById(hwId: number): string {
    const match = hostnameList.find((hw) => hw.id === hwId);
    return match ? match.hostname : "";
  }

  const handleAddHardware = () => {
    setEditMode(false);
    const newForm = { ...defaultHwForm };

    // If your user already picked a DC from the top-level dropdown,
    // find the matching data center object (skip if "All" is selected):
    if (selectedDataCenterName !== "All") {
      const foundDC = dataCenters.find(
        (dc: DataCenterItem) =>
          `${dc.datacenterId} - ${dc.displayName}` === selectedDataCenterName
      );
      if (foundDC) {
        // Put its ID in our form, so we can use it in the modal
        newForm.datacenterId = foundDC.datacenterId;
      }
    }

    // Now set that form into state
    setHwForm(newForm);
    setSubTypeList([]); // Al crear, subType en blanco
    setIsModalOpen(true);
  };

  const handleEditHardware = (hw: Hardware) => {
    setEditMode(true);
    setIsModalOpen(true);
    setIsEditLoading(true);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await getHardwareById(token, String(hw.id));
        const detail = (resp as any)?.data?.data ?? (resp as any)?.data ?? hw;
        // Extract typeHardwareId and subtypeHardwareId from the response
        const typeHardwareId = detail.typeHardwareId || (detail.typeHardware?.id ? detail.typeHardware.id : "");
        const subtypeHardwareId = detail.subtypeHardwareId || (detail.subtypeHardware?.id ? detail.subtypeHardware.id : "");
        
        setHwForm({
          ...defaultHwForm,
          id: String(detail.id),
          datacenterId: detail.datacenterId || "",
          typeHardware: detail.typeHardware?.name || detail.typeHardware || "",
          typeHardwareId: typeHardwareId,
          serialNumber: detail.serialNumber || "",
          model: detail.model || "",
          vendor: detail.vendor || "",
          hostname: detail.hostname || "",
          installedDate: detail.installedDate || "",
          endOfLifeDate: detail.endOfLifeDate || "",
          rackId: detail.rackId || "",
          rackUnit: detail.rackUnit ? String(detail.rackUnit) : "",
          status: detail.status || "",
          phase: detail.phase || "",
          template: detail.template || "",
          subtypeHardware: detail.subtypeHardware?.name || detail.subtypeHardware || "",
          subtypeHardwareId: subtypeHardwareId,
        });
        setSubTypeList([]);
        
        // If it's a Module type, fetch hostname options
        const typeName = detail.typeHardware?.name || detail.typeHardware || "";
        if (typeName === "Module" && detail.datacenterId) {
          fetchHostnameOptions(detail.datacenterId);
          // Set the current hostname ID if available
          if (detail.targetDeviceId) {
            setHostnameId(detail.targetDeviceId);
          }
          if (detail.slotAssignmentId) {
            setSlotId(detail.slotAssignmentId);
          }
        }

        // Fetch subtypes for the current type so they're available in the dropdown
        if (typeHardwareId && typeof typeHardwareId === "number") {
          try {
            setIsLoadingSubtypes(true);
            const resp = await getSubTypesByTypeHardware(token, typeHardwareId);
            const subtypesRaw = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
            setSubTypeList(subtypesRaw);
          } catch (error) {
            console.error("Error fetching subtypes for edit =>", error);
            setSubTypeList([]);
          } finally {
            setIsLoadingSubtypes(false);
          }
        }
      } catch (error) {
        console.error("Error fetching hardware detail:", error);
      } finally {
        setIsEditLoading(false);
      }
    })();
  };

  const handleViewHardware = (hw: Hardware) => {
    setIsViewLoading(true);
    setSelectedHardware(null);
    setIsViewModalOpen(true);
    (async () => {
      try {
        const token = await getAccessTokenSilently();
        const resp = await getHardwareById(token, String(hw.id));
        const detail = (resp as any)?.data?.data ?? (resp as any)?.data ?? hw;
        setSelectedHardware(detail as Hardware);
      } catch (error) {
        console.error("Error fetching hardware detail:", error);
        setSelectedHardware(hw);
      } finally {
        setIsViewLoading(false);
      }
    })();
  };

  const handleDeleteClick = (hw: Hardware) => {
    setHardwareToDelete(hw);
    setIsAlertOpen(true);
  };

  const handleConfirmDelete = async () => {
    if (!hardwareToDelete) return;

    try {
      setIsDeleting(true);
      const token = await getAccessTokenSilently();
      await deleteHardwareResource(token, String(hardwareToDelete.id));
      
      // Close the alert first
      setIsAlertOpen(false);
      setHardwareToDelete(null);
      
      // Refetch the hardware list based on current selection
      if (selectedDataCenterName === "All") {
        // Fetch all hardware
        try {
          setIsFetching(true);
          const resp = await getAllHardware(token);
          const hwRaw = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
          setHardwareList(hwRaw);
        } catch (err) {
          console.error("Error fetching all hardware after delete:", err);
        } finally {
          setIsFetching(false);
        }
      } else {
        // Fetch hardware for selected datacenter
        const foundDC = dataCenters.find(
          (dc: DataCenterItem) =>
            `${dc.datacenterId} - ${dc.displayName}` === selectedDataCenterName
        );
        if (foundDC) {
          try {
            setIsFetching(true);
            const resp = await getHardwareByDatacenterId(token, foundDC.datacenterId);
            const hwRaw = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
            setHardwareList(hwRaw);
          } catch (err) {
            console.error("Error fetching hardware by DC after delete:", err);
          } finally {
            setIsFetching(false);
          }
        }
      }
    } catch (err) {
      console.error("Error deleting hardware:", err);
    } finally {
      setIsDeleting(false);
    }
  };

  const handleCancelDelete = () => {
    setHardwareToDelete(null);
    setIsAlertOpen(false);
  };

  // GUARDAR hardware (Add/Edit)
  const handleSaveHardware = async () => {
    try {
      if (editMode) {
        setIsSaving(true);
      } else {
        setIsCreating(true);
      }
      const token = await getAccessTokenSilently();

      // Get datacenter info from selection
      let datacenterId = hwForm.datacenterId;
      let datacenterName = "";
      
      // Find the selected datacenter to get both ID and displayName
      const foundDC = dataCenters.find(
        (dc: DataCenterItem) => {
          if (editMode) {
            return dc.datacenterId === hwForm.datacenterId;
          } else {
            return `${dc.datacenterId} - ${dc.displayName}` === selectedDataCenterName;
          }
        }
      );
      
      if (foundDC) {
        datacenterId = foundDC.datacenterId;
        datacenterName = foundDC.displayName;
      } else if (!editMode && selectedDataCenterName !== "All") {
        // Fallback: try to parse from selectedDataCenterName
        const match = selectedDataCenterName.match(/^(.+?)\s*-\s*(.+)$/);
        if (match) {
          datacenterId = match[1].trim();
          datacenterName = match[2].trim();
        }
      }

      // Get type from subtype (subtype name becomes the "type" field)
      const typeName = typeof hwForm.subtypeHardware === "object" && hwForm.subtypeHardware !== null
        ? hwForm.subtypeHardware.name
        : hwForm.subtypeHardware || "";

      // Construct payload according to API requirements
      const payload: AddHardwareResourcePayload = {
        datacenterId: datacenterId,
        name: datacenterName, // Datacenter displayName
        type: typeName, // Subtype name
        serialNumber: hwForm.serialNumber,
        model: hwForm.model,
        vendor: hwForm.vendor,
        hostname: hwForm.hostname,
        installedDate: hwForm.installedDate,
        endOfLifeDate: hwForm.endOfLifeDate,
        status: hwForm.status,
      };

      // Add optional fields only if they have values
      if (hwForm.rackId) {
        payload.rackId = hwForm.rackId;
      }
      if (hwForm.rackUnit) {
        payload.rackUnit = hwForm.rackUnit;
      }
      if (hwForm.phase) {
        payload.phase = hwForm.phase;
      }
      if (hwForm.template) {
        payload.template = hwForm.template;
      }

      // If Module type, add targetDeviceId and slotAssignmentId
      if (hwForm.typeHardware === "Module") {
        payload.targetDeviceId = hostnameId;
        payload.slotAssignmentId = slotId;
      }

      if (editMode && hwForm.id) {
        // EDIT - refresh the list after update
        await updateHardwareResource(token, hwForm.id, payload);
        if (payload.datacenterId) {
          const r = await getHardwareByDatacenterId(
            token,
            payload.datacenterId
          );
          const hwRaw = (r as any)?.data?.data ?? (r as any)?.data ?? [];
          setHardwareList(hwRaw);
        }
      } else {
        // CREATE
        await addHardwareResource(token, payload);
        if (payload.datacenterId) {
          const r = await getHardwareByDatacenterId(
            token,
            payload.datacenterId
          );
          const hwRaw = (r as any)?.data?.data ?? (r as any)?.data ?? [];
          setHardwareList(hwRaw);
        }
      }
    } catch (err) {
      console.error("Error saving hardware resource:", err);
    } finally {
      setIsSaving(false);
      setIsCreating(false);
      setIsModalOpen(false);
    }
  };

  // ===== 4.1) HANDLERS: Type & SubType =====
  const handleSelectType = async (
    selectedTypeName: string | { name: string }
  ) => {
    let typeName: string;

    if (typeof selectedTypeName === "object") {
      // pick the .name field
      typeName = selectedTypeName.name;
    } else {
      typeName = selectedTypeName; // it's already a string
    }

    if (typeName === "Module" && hwForm.datacenterId) {
      fetchHostnameOptions(hwForm.datacenterId);
    }
    // Buscamos el ID del type a partir de su name
    const foundTypeObj = typeHardwareList.find(
      (t) => t.name === selectedTypeName
    );
    if (!foundTypeObj) {
      // No encontrado => sin subtypes
      setSubTypeList([]);
      return;
    }
    setHwForm((prev) => ({
      ...prev,
      typeHardware: typeName,
      typeHardwareId: foundTypeObj?.id,
      subType: "",
      subtypeHardware: "",
      subtypeHardwareId: "",
    }));

    // Llamamos al endpoint GET /typeHardware/{id}/subtypes
    try {
      setIsLoadingSubtypes(true);
      const token = await getAccessTokenSilently();
      const resp = await getSubTypesByTypeHardware(token, foundTypeObj.id);
      // Response shape: { success, message, data: [...] }
      const subtypesRaw = (resp as any)?.data?.data ?? (resp as any)?.data ?? [];
      setSubTypeList(subtypesRaw);
    } catch (error) {
      console.error("Error fetching subtypes =>", error);
      setSubTypeList([]);
    } finally {
      setIsLoadingSubtypes(false);
    }
  };

  function getTypeHardwareString(value: string | { name: string } | null) {
    if (!value) return "";
    if (typeof value === "object") return value.name;
    return value; // it's a string
  }

  const handleSelectSubType = (selectedSubType: string | { name: string }) => {
    let typeName: string;

    if (typeof selectedSubType === "object") {
      // pick the .name field
      typeName = selectedSubType.name;
    } else {
      typeName = selectedSubType; // it's already a string
    }
    // Find the selected subtype object
    const foundSubTypeObj = subTypeList.find((s) => s.name === typeName);
    setHwForm((prev) => ({
      ...prev,
      subtypeHardware: typeName,
      subtypeHardwareId: foundSubTypeObj?.id ?? 0,
    }));
  };


  // ===== 6) COLUMNAS DE HARDWARE =====
  const showDatacenterColumn = selectedDataCenterName === "All";
  const columns = getHardwareColumns({
    handleViewHardware,
    handleEditHardware,
    handleDeleteClick,
    showDatacenterColumn,
  });

  // Excluimos “type” y “subType” del form (mostramos manual en el modal)
  const hardwareFormFields = [
    {
      key: "serialNumber",
      label: "SerialNumber",
      placeholder: "e.g. FOC2625R1MM",
    },
    { key: "model", label: "Model", placeholder: "e.g. NCS-5501-SE" },
    { key: "vendor", label: "Vendor", placeholder: "e.g. CISCO" },
    {
      key: "hostname",
      label: "Hostname",
      placeholder: "e.g. DC001-ROUTER-PE1",
    },
    {
      key: "installedDate",
      label: "Installed Date",
      placeholder: "YYYY-MM-DD",
    },
    { key: "endOfLifeDate", label: "EndOfLifeDate", placeholder: "YYYY-MM-DD" },
    { key: "rackId", label: "RackId", placeholder: "e.g. RACK-001" },
    { key: "rackUnit", label: "rackUnit", placeholder: "e.g. 46" },
    { key: "status", label: "Status", placeholder: "Select status" },
    { key: "phase", label: "Phase", placeholder: "e.g. 3-phase" },
    { key: "template", label: "Template", placeholder: "e.g. NCS5501" },
  ] as const;

  // ===== 7) DC Name for modal =====
  const currentDcName = useMemo(() => {
    if (hwForm.datacenterId) {
      const found = dataCenters.find(
        (dc: DataCenterItem) => dc.datacenterId === hwForm.datacenterId
      );
      if (found) {
        // Return in the format "datacenterId - displayName" to match dropdown options
        return `${found.datacenterId} - ${found.displayName}`;
      }
    }
    // If no datacenter is set in the form, use the selected one from the main dropdown
    if (selectedDataCenterName && selectedDataCenterName !== "All") {
      return selectedDataCenterName;
    }
    return "";
  }, [hwForm.datacenterId, dataCenters, selectedDataCenterName]);

  // Handler para cambiar campos con setHwForm
  const handleFieldChange = (
    fieldKey: keyof MyHardwareForm,
    newValue: string
  ) => {
    setHwForm((prev) => ({ ...prev, [fieldKey]: newValue }));
  };

  // Filter fields for GenericSearchbar
  const hardwareFields: FilterField<Hardware>[] = useMemo(() => {
    const fields: FilterField<Hardware>[] = [
      { label: "ID", accessor: (row) => String(row.id), type: "string" },
    ];
    
    // Add Data Center ID field only when "All" is selected
    if (selectedDataCenterName === "All") {
      fields.push({ label: "Data Center ID", accessor: (row) => row.datacenterId, type: "string" });
    }
    
    fields.push(
      { label: "Hostname", accessor: (row) => row.hostname, type: "string" },
      { 
        label: "Type + Subtype", 
        accessor: (row) => {
          const typeName = row.typeHardware 
            ? (typeof row.typeHardware === "object" ? row.typeHardware.name : row.typeHardware)
            : "";
          const subtypeName = row.subtypeHardware
            ? (typeof row.subtypeHardware === "object" ? row.subtypeHardware.name : row.subtypeHardware)
            : "";
          return [typeName, subtypeName].filter(Boolean).join(" + ");
        }, 
        type: "string" 
      },
      { label: "Serial Number", accessor: (row) => row.serialNumber, type: "string" },
      { label: "Vendor", accessor: (row) => row.vendor || "", type: "string" },
      { label: "Status", accessor: (row) => row.status, type: "string" },
    );
    
    return fields;
  }, [selectedDataCenterName]);

  const keyMap = useMemo(() => {
    const m = new Map<string, string>();
    for (const f of hardwareFields) {
      const id = (f.key ?? labelId(f.label)).toLowerCase();
      const label = f.label.toLowerCase();
      m.set(id, id);
      m.set(label, id);
    }
    return m;
  }, [hardwareFields]);

  const fieldById = useMemo(() => {
    const m = new Map<string, FilterField<Hardware>>();
    for (const f of hardwareFields) {
      const id = f.key ?? labelId(f.label);
      m.set(id.toLowerCase(), f);
    }
    return m;
  }, [hardwareFields]);

  const normalizedFilters = useMemo(() => {
    return activeFilters.map((f) => {
      const token = f.op ? { ...f } : parseToken(f.key ? `${f.key}=${f.value}` : f.value);

      if (token.key) {
        const k = token.key.trim().toLowerCase();
        token.key = keyMap.get(k) ?? labelId(token.key);
      }

      const field = token.key ? fieldById.get(token.key) : undefined;
      if (field?.type === "string" && token.value) {
        const quoted = token.value.match(/^"(.*)"$/);
        if (quoted) {
          token.value = quoted[1];
          token.op = "=";
        } else if (!token.op) {
          token.op = ":"; // contains
        }
      }
      return token;
    });
  }, [activeFilters, keyMap, fieldById]);

  const predicate = useMemo(
    () => buildPredicate<Hardware>(normalizedFilters, hardwareFields),
    [normalizedFilters, hardwareFields]
  );

  const filteredHardware = useMemo(() => {
    if (!predicate) return hardwareList;
    return hardwareList.filter(predicate);
  }, [hardwareList, predicate]);

  const suggestedKeys = useMemo(() => {
    const headers = hardwareFields.map((f) => f.label);

    if (isViewingAllCustomers && !headers.includes("Customer")) {
      headers.splice(1, 0, "Customer");
    }
    return headers;
  }, [hardwareFields, isViewingAllCustomers]);

  // ===== 8) RENDER =====
  return (

    <div className="relative p-4 overflow-hidden flex flex-col h-full">
      {/* 1) SELECT DATACENTER */}
      <div className="flex-shrink-0 mb-4">
        <GenericCard isCollapsible={false}>
          <div className="flex items-center justify-between p-5">
            <div className="flex flex-col">
              <label className="mb-1 text-sm font-medium text-semantic-text-secondary">
                Select a Data Center
              </label>
              <GenericDropdown
                items={dataCenterOptions}
                defaultLabel="Select a data center"
                value={selectedDataCenterName}
                onSelect={handleDataCenterSelect}
                minWidthClass="min-w-[300px]"
                maxWidthClass="max-w-[400px]"
              />
            </div>
          </div>
        </GenericCard>
      </div>

      {/* 2) TABLA HARDWARE */}
      <div className="relative flex-1 min-h-0">
        <GenericCard
          isCollapsible={false}
          className="h-full flex flex-col"
          bodyContent={
            <div className="flex flex-col h-full min-h-0">
              {/* Searchbar y botón */}
              <div className="mb-4 bg-[var(--semantic-bg-primary)] flex-shrink-0">
                <div className="flex items-center justify-between gap-4 my-5">
                  <div className="w-[520px]">
                    <GenericSearchbar
                      placeholder="Find by attribute or value"
                      suggestedKeys={suggestedKeys}
                      onFiltersChange={setActiveFilters}
                    />
                  </div>
                  <GenericButton
                    label={
                      <div className="flex items-center gap-2">
                        <IconPlus className="w-5 h-5" />
                        <span>Add new hardware</span>
                      </div>
                    }
                    variant="brand"
                    size="md"
                    onClick={handleAddHardware}
                  />
                </div>
              </div>

              <div className="flex-1 min-h-0 flex flex-col">
                {isFetching && selectedDataCenterName ? (
                  <TableSkeleton columns={14} rows={5} />
                ) : (
                  <GenericTable
                    columns={columns}
                    data={filteredHardware}
                    width="100%"
                    initialRowsPerPage={10}
                    rowsPerPageOptions={[10, 20, 30]}
                    rowsPerPage={rowsPerPage}
                    onRowsPerPageChange={setRowsPerPage}
                    tableClassName="bg-semantic-bg-primary"
                    maxTableBodyHeight="100%"
                  />
                )}
              </div>
            </div>
          }
        />
      </div>

        {/* 3) MODAL => Add/Edit hardware */}
        <HardwareEditModal
          isOpen={isModalOpen}
          isLoading={isEditLoading}
          editMode={editMode}
          hwForm={hwForm}
          currentDcName={currentDcName}
          dataCenterOptions={dataCenterOptions.filter((opt) => opt !== "All")}
          typeHardwareOptions={typeHardwareOptions}
          subTypeOptions={subTypeOptions}
          hostnameOptions={hostnameOptions}
          slotOptions={slotOptions}
          hostnameId={hostnameId}
          slotId={slotId}
          isLoadingSubtypes={isLoadingSubtypes}
          hardwareFormFields={hardwareFormFields}
          getTypeHardwareString={getTypeHardwareString}
          isSaving={isSaving}
          isCreating={isCreating}
          onClose={() => setIsModalOpen(false)}
          onSave={handleSaveHardware}
          onFieldChange={handleFieldChange}
          onDataCenterSelect={handleModalDataCenterSelect}
          onSelectType={handleSelectType}
          onSelectSubType={handleSelectSubType}
          onHostnameSelect={handleHostnameSelect}
          onSlotSelect={handleSlotSelect}
          getHostnameLabelById={getHostnameLabelById}
          getSlotLabelById={getSlotLabelById}
        />

        {/* 4) MODAL => View hardware */}
        <HardwareViewModal
          isOpen={isViewModalOpen}
          isLoading={isViewLoading}
          hardware={selectedHardware}
          onClose={() => setIsViewModalOpen(false)}
          getTypeHardwareString={getTypeHardwareString}
        />

        {/* 5) ALERT => confirm Delete hardware */}
        <GenericAlert
          isOpen={isAlertOpen}
          onClose={handleCancelDelete}
          onConfirm={handleConfirmDelete}
          title="Are you sure?"
          description="This will permanently delete the hardware from the inventory."
          cancelLabel="Cancel"
          confirmLabel={isDeleting ? "Deleting..." : "Delete"}
          confirmButtonDisabled={isDeleting}
        />

    </div>
  );
}
